/**
 * Lightweight, transport-agnostic telemetry bus for the CMS resilience
 * layer.
 *
 *   emit(name, payload)  — fire an event
 *   subscribe(listener)  — receive ALL events
 *
 * The layer emits structured events (see EVENTS below) but does not know
 * WHERE they go. Later a shim can pipe these to Prometheus, OpenTelemetry,
 * console, Grafana, Sentry, or any log collector without touching business
 * logic. A `window` CustomEvent is also dispatched for legacy DOM listeners
 * (e.g. dev tools, Playwright tests).
 */

export const CMS_EVENTS = Object.freeze({
  SOURCE:            "cms:source",             // { url, source: cms|cache|fallback, ms }
  FETCH_START:       "cms:fetch-start",        // { url, attempt }
  FETCH_SUCCESS:     "cms:fetch-success",      // { url, ms, attempt }
  FETCH_FAILURE:     "cms:fetch-failure",      // { url, error, attempt }
  BREAKER_OPEN:      "cms:breaker-open",       // { url, consecutiveFailures }
  BREAKER_CLOSE:     "cms:breaker-close",      // { url }
  BREAKER_HALF_OPEN: "cms:breaker-half-open",  // { url }
  BREAKER_TRIP:      "cms:breaker-trip",       // { url } — fail-fast rejection
  VERSION_MISMATCH:  "cms:version-mismatch",   // { url, cached, expected }
  CACHE_HIT:         "cms:cache-hit",          // { url, key }
  CACHE_MISS:        "cms:cache-miss",         // { url, key }
  CACHE_INVALIDATE:  "cms:cache-invalidate",   // { key, reason }
});

const listeners = new Set();

/** Subscribe to every telemetry event. Returns an unsubscribe function. */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Emit an event to all subscribers + dispatch a DOM CustomEvent (browser only). */
export function emit(name, payload = {}) {
  const evt = { name, payload, ts: Date.now() };
  // 1. In-app subscribers
  listeners.forEach((fn) => {
    try { fn(evt); } catch { /* swallow */ }
  });
  // 2. DOM bridge — lets external code observe events without importing
  // anything from the frontend bundle.
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: payload }));
    } catch { /* swallow */ }
  }
}

/** Small helper: read the last N events (useful for tests / dashboards). */
const _ring = [];
subscribe((evt) => {
  _ring.push(evt);
  if (_ring.length > 200) _ring.shift();
});
export function recentEvents() {
  return _ring.slice();
}
